/**
 * window.__synonyms — data access for the synonym-finder tool.
 *
 * Merges TWO isolated datasets, each with its own license, never mixing them on disk:
 *   • public/data/thesaurus/      — our own alt-derived, curated synonyms (our license)
 *   • public/data/thesaurus-ext/  — Wiktionary-derived synonyms (CC-BY-SA, via kaikki.org)
 * Both share the same shape (word -> [{syn:[], …}]) and the same shardOf, so one loader
 * serves both. Results carry their `src` so the UI can attribute Wiktionary correctly.
 *
 * A group is ONE SENSE of one article, and carries what the page needs to say so:
 *   g   — the sense gloss («место, где кто-либо постоянно проживает»)
 *   t   — canonical register tags (slang / obsolete / figurative / colloquial …)
 *   d   — subject domains (комп., спорт., мед.)
 *   via — set when the relation was stated in ANOTHER article: «дом → малина» exists
 *         because «малина» lists «дом», not the other way round. Without this the page
 *         cannot explain the group and it reads as a defect (bug report 2026-07-24).
 *
 * SUGGESTION source, never authority: a missing manifest / shard / word resolves to an empty
 * result, never throws. Loaded per-tool (from the tool blade), not globally.
 *
 *   await window.__synonyms.lookup(lang, word)
 *     -> { word, groups: [ { syn:[{w, src}], pos, g, t, d, via, src } ], sources, found }
 */
(function () {
    'use strict';
    if (window.__synonyms) return;

    // Cyrillic-only shard (our own thesaurus, small) vs finer ext shard (Wiktionary, huge).
    // Each MUST mirror the matching build script's shardOf exactly.
    function shardOfOurs(word) {
        var c = (String(word)[0] || '_').toLowerCase();
        return [/[а-яё]/.test(c) ? c : '_'];
    }
    // MUST mirror extShardCandidates() in scripts/build-thesaurus-ext.cjs: a shard that grew
    // past the size budget is split by a LONGER character prefix, so «полка» may live in
    // «пол.json» rather than «п.json» (which alone reached 7 MB once every group carried its
    // sense gloss). Candidates are most-specific-first; the caller keeps the first one the
    // manifest actually lists — the manifest is what records how deep each letter was split.
    var SHARD_CHAR = /[a-zа-яёÀ-ɏ؀-ۿݐ-ݿ]/;
    var SHARD_MAX_PREFIX = 4;
    // Windows reserved device names get a «-» suffix in the shard FILE name («con» → «con-»):
    // git cannot index a file literally named con.json. Mirrors safeShard() in the builder.
    var WIN_RESERVED = /^(con|prn|aux|nul|com[1-9]|lpt[1-9])$/i;
    function shardOfExt(word) {
        var s = String(word || '');
        var ch = s[0];
        if (!ch) return ['_'];
        var cp = ch.codePointAt(0);
        if (cp >= 0xAC00 && cp <= 0xD7A3) return ['k' + Math.floor((cp - 0xAC00) / 588)];
        var c = ch.toLowerCase();
        if (!SHARD_CHAR.test(c)) return ['_'];
        var low = s.toLowerCase();
        var out = [];
        for (var len = Math.min(SHARD_MAX_PREFIX, low.length); len >= 2; len--) {
            var p = low.slice(0, len), ok = true;
            for (var i = 0; i < p.length; i++) { if (!SHARD_CHAR.test(p[i])) { ok = false; break; } }
            if (ok) out.push(WIN_RESERVED.test(p) ? p + '-' : p);
        }
        out.push(c);
        return out;
    }
    var BASES = [
        { dir: '/data/thesaurus/', src: 'ours', shardOf: shardOfOurs },
        { dir: '/data/thesaurus-ext/', src: 'wiktionary', shardOf: shardOfExt },
    ];
    var manifests = {};   // base.dir -> Promise<manifest|null>
    var shardCache = {};  // "base|lang/shard" -> Promise<obj>

    function loadManifest(base) {
        if (manifests[base.dir]) return manifests[base.dir];
        manifests[base.dir] = fetch(base.dir + 'manifest.json', { cache: 'no-cache' })
            .then(function (r) { return r.ok ? r.json() : null; })
            .catch(function () { return null; });
        return manifests[base.dir];
    }
    function loadShard(base, lang, dir, shard, version) {
        var key = base.dir + '|' + lang + '/' + shard;
        if (shardCache[key]) return shardCache[key];
        var url = base.dir + (dir || lang) + '/' + shard + '.json?v=' + encodeURIComponent(version || 'x');
        shardCache[key] = fetch(url, { cache: 'force-cache' })
            .then(function (r) { return r.ok ? r.json() : {}; })
            .catch(function () { return {}; });
        return shardCache[key];
    }

    function fromBase(base, lang, word) {
        return loadManifest(base).then(function (m) {
            var meta = m && m.languages && m.languages[lang];
            if (!meta) return [];
            var shard = null;
            var cands = base.shardOf(word);
            for (var i = 0; i < cands.length; i++) {
                if (!meta.shards || meta.shards.indexOf(cands[i]) !== -1) { shard = cands[i]; break; }
            }
            if (shard === null) return [];
            return loadShard(base, lang, meta.file_dir, shard, m.version).then(function (data) {
                // Case-insensitive key resolution. Keys are stored in lemma case —
                // lowercase for most langs, but Capitalised for German nouns (Haus) —
                // while mobile keyboards AUTO-CAPITALISE the first letter, so a user
                // typing «Великий» must still hit the lowercase «великий» key. Try
                // exact → lowercase → Capitalised, first hit wins (never merge, so a
                // proper noun «Роза» can't bleed into the flower «роза»).
                var senses = null;
                if (data) {
                    var wl = word.toLowerCase();
                    var wc = wl.charAt(0).toUpperCase() + wl.slice(1);
                    senses = data[word] || data[wl] || (wc !== word && wc !== wl ? data[wc] : null) || null;
                }
                if (!senses) return [];
                // normalise to [{syn:[...], pos, g, t, d, via, src}] — `pos` is present on the
                // Wiktionary base; a thesaurus must not show «happy» the verb under «happy»
                // the adjective. `g`/`t`/`d`/`via` are absent on our own curated base.
                return senses.map(function (s) {
                    return {
                        syn: (s.syn || []).slice(), pos: s.pos || '', src: base.src,
                        g: s.g || '', t: (s.t || []).slice(), d: (s.d || []).slice(), via: s.via || '',
                    };
                });
            });
        }).catch(function () { return []; });
    }

    function lookup(lang, word) {
        word = String(word == null ? '' : word).trim();
        var empty = { word: word, groups: [], sources: { ours: 0, wiktionary: 0 }, found: false };
        if (!word) return Promise.resolve(empty);
        return Promise.all(BASES.map(function (b) { return fromBase(b, lang, word); }))
            .then(function (perBase) {
                var wl = word.toLowerCase();
                var groups = [];
                var groupKeys = {};          // dedup whole GROUPS, not individual synonyms
                var sources = { ours: 0, wiktionary: 0 };
                // ours first (authoritative), then wiktionary (breadth)
                perBase.forEach(function (senseList) {
                    senseList.forEach(function (sense) {
                        var items = [];
                        var seen = {};       // dedup WITHIN a sense only — «жилище» belongs to
                                             // several senses of «дом», and a global dedup
                                             // silently emptied the later ones.
                        (sense.syn || []).forEach(function (w) {
                            var lw = String(w).toLowerCase();
                            if (lw === wl || seen[lw]) return;
                            seen[lw] = 1;
                            items.push({ w: w, src: sense.src });
                        });
                        if (!items.length) return;
                        var key = sense.src + '|' + (sense.pos || '') + '|' + (sense.t || []).join(',') +
                            '|' + items.map(function (i) { return i.w.toLowerCase(); }).join('|');
                        if (groupKeys[key]) return; groupKeys[key] = 1;
                        sources[sense.src] = (sources[sense.src] || 0) + items.length;
                        groups.push({
                            syn: items, pos: sense.pos || '', src: sense.src,
                            g: sense.g || '', t: sense.t || [], d: sense.d || [], via: sense.via || '',
                        });
                    });
                });
                return { word: word, groups: groups, sources: sources, found: groups.length > 0 };
            });
    }

    // flat, deduped list of just the synonym strings (convenience)
    function flat(lang, word) {
        return lookup(lang, word).then(function (r) {
            var out = [], seen = {};
            r.groups.forEach(function (g) { g.syn.forEach(function (it) { if (!seen[it.w.toLowerCase()]) { seen[it.w.toLowerCase()] = 1; out.push(it.w); } }); });
            return out;
        });
    }

    window.__synonyms = { lookup: lookup, flat: flat };
})();
